import { requisicaoAutenticada } from './http';
import type { FonteDaVariavel, OpcaoDeFonte, VariavelReconhecida } from './modelosDeContrato';

/** Os modelos de e-mail (D-017) — espelha `apps/api/src/modules/email/modelos`. */

export interface Moldura {
  readonly nome: string;
  readonly logoUrl: string | null;
  readonly cor: string;
  readonly rodape: string;
}

export interface VariavelDoEmail {
  readonly nome: string;
  readonly rotulo: string;
  /** HTML pronto: entra com três chaves. */
  readonly html: boolean;
}

export interface EmailDoDeca {
  readonly id: string;
  readonly grupo: string;
  readonly rotulo: string;
  readonly quando: string;
  readonly variaveis: readonly VariavelDoEmail[];
  /** Criado aqui ("+ Novo e-mail"): sai só pelas automações. */
  readonly personalizado: boolean;
  /** O template que o Postmark usa hoje; `null` = texto simples automático. */
  readonly templatePostmark: number | null;
  readonly modelo: {
    readonly assunto: string;
    readonly corpo: string;
    readonly ativo: boolean;
    readonly atualizadoEm: string;
  } | null;
  readonly problemas: readonly string[];
  /** A automação do e-mail do sistema; `null` nos e-mails novos e nos do fluxo mensal. */
  readonly automacao: {
    readonly gatilho: string;
    readonly paraQuem: string;
    readonly aoDesligar: string;
    readonly paraEmpresa: boolean;
    readonly diasAntes: boolean;
    readonly regra: RegraDoEmail;
    /** O dia em que o fluxo mensal agenda este e-mail; `null` nos que não têm data. */
    readonly data: {
      readonly quando: QuandoDoEmail;
      readonly descricao: string;
      readonly mesDeReferencia: string;
    } | null;
  } | null;
  /** Do fluxo mensal do Recorrente (o cronograma). */
  readonly cronograma: boolean;
}

/** Um dia dentro do mês: o n-ésimo dia útil, um dia do calendário ou o último dia útil. */
export type DiaDoMes =
  | { readonly tipo: 'DIA_UTIL'; readonly n: number }
  | { readonly tipo: 'DIA_DO_MES'; readonly dia: number }
  | { readonly tipo: 'ULTIMO_DIA_UTIL' };

/** A data de um e-mail do fluxo mensal — a forma depende do e-mail. */
export type QuandoDoEmail =
  | { readonly diasAposFechamento: number }
  | { readonly dia: DiaDoMes; readonly pisoPercentual?: number };

export function descreverDia(dia: DiaDoMes): string {
  switch (dia.tipo) {
    case 'DIA_UTIL':
      return `${dia.n}º dia útil`;
    case 'DIA_DO_MES':
      return `dia ${dia.dia}`;
    case 'ULTIMO_DIA_UTIL':
      return 'último dia útil';
  }
}

/** Se o e-mail do sistema sai, para quem, cópias e antecedência. */
export interface RegraDoEmail {
  readonly ativa: boolean;
  readonly para: 'SISTEMA' | 'FUNCAO' | 'SO_COPIAS';
  readonly funcao: string | null;
  readonly copias: readonly string[];
  readonly diasAntes: number | null;
}

export const salvarRegra = (
  tipo: string,
  regra: RegraDoEmail & { readonly quando?: QuandoDoEmail; readonly reagendar?: boolean },
): Promise<{ reagendados: number; semCalculo: number }> =>
  requisicaoAutenticada(`/emails/modelos/${encodeURIComponent(tipo)}/regra`, {
    metodo: 'PUT',
    corpo: regra,
  });

/** Quantos agendamentos deste e-mail ainda vão sair — a tela pergunta antes de reagendar. */
export const carregarAgendados = (tipo: string): Promise<{ futuros: number }> =>
  requisicaoAutenticada(`/emails/modelos/${encodeURIComponent(tipo)}/agendados`);

export interface VersaoDoModelo {
  readonly id: string;
  readonly assunto: string;
  readonly corpo: string;
  readonly motivo: string;
  readonly criadaEm: string;
  readonly autor: string | null;
}

export interface TemplateDoPostmark {
  readonly tipo: string;
  readonly templatePostmark: number;
  readonly assunto: string;
  readonly corpo: string;
}

export interface TextoDoModelo {
  readonly assunto: string;
  readonly corpo: string;
}

export const CHAVE_DOS_MODELOS = ['emails', 'modelos'] as const;

/** Como a variável entra no texto: três chaves para o HTML pronto. */
export const comoInserir = (v: VariavelDoEmail): string =>
  v.html ? `{{{${v.nome}}}}` : `{{${v.nome}}}`;

export const carregarModelos = (): Promise<{ moldura: Moldura; emails: EmailDoDeca[] }> =>
  requisicaoAutenticada('/emails/modelos');

export const carregarDoPostmark = (): Promise<TemplateDoPostmark[]> =>
  requisicaoAutenticada('/emails/modelos/postmark');

export const salvarModelo = (
  tipo: string,
  dados: TextoDoModelo & {
    readonly templatePostmark?: number;
    readonly motivo?: 'IMPORTADO' | 'EDITADO' | 'RESTAURADO';
  },
): Promise<void> =>
  requisicaoAutenticada(`/emails/modelos/${encodeURIComponent(tipo)}`, {
    metodo: 'PUT',
    corpo: dados,
  });

export const alternarModelo = (tipo: string, ativo: boolean): Promise<void> =>
  requisicaoAutenticada(`/emails/modelos/${encodeURIComponent(tipo)}`, {
    metodo: 'PATCH',
    corpo: { ativo },
  });

export const carregarVersoes = (tipo: string): Promise<VersaoDoModelo[]> =>
  requisicaoAutenticada(`/emails/modelos/${encodeURIComponent(tipo)}/versoes`);

export const previaDoModelo = (
  dados: TextoDoModelo & { readonly tipo: string; readonly moldura?: MolduraNaTela },
): Promise<{ assunto: string; html: string; problemas: string[] }> =>
  requisicaoAutenticada('/emails/modelos/previa', { metodo: 'POST', corpo: dados });

export const testarModelo = (
  dados: TextoDoModelo & { readonly tipo: string },
): Promise<{ destinatario: string; ensaio: boolean }> =>
  requisicaoAutenticada('/emails/modelos/teste', { metodo: 'POST', corpo: dados });

/** A moldura como a tela a edita: sem logo é texto vazio. */
export interface MolduraNaTela {
  readonly nome: string;
  readonly logoUrl: string;
  readonly cor: string;
  readonly rodape: string;
}

export const salvarMoldura = (dados: MolduraNaTela): Promise<Moldura> =>
  requisicaoAutenticada('/emails/modelos/moldura', { metodo: 'PUT', corpo: dados });

export const criarNovoEmail = (nome: string): Promise<{ tipo: string }> =>
  requisicaoAutenticada('/emails/modelos', { metodo: 'POST', corpo: { nome } });

export const renomearEmail = (tipo: string, nome: string): Promise<void> =>
  requisicaoAutenticada(`/emails/modelos/${encodeURIComponent(tipo)}/nome`, {
    metodo: 'PATCH',
    corpo: { nome },
  });

export const excluirEmail = (tipo: string): Promise<void> =>
  requisicaoAutenticada(`/emails/modelos/${encodeURIComponent(tipo)}`, { metodo: 'DELETE' });

export type SituacaoDoEnvio =
  'AGENDADO' | 'ENVIANDO' | 'ENVIADO' | 'ENSAIO' | 'FALHOU' | 'CANCELADO';

export interface EnvioDoEmail {
  readonly id: string;
  readonly automacao: string;
  readonly empresa: string | null;
  readonly enviarEm: string;
  readonly situacao: SituacaoDoEnvio;
  readonly destinatarios: readonly string[];
  readonly erro: string | null;
}

export const carregarEnvios = (tipo: string): Promise<EnvioDoEmail[]> =>
  requisicaoAutenticada(`/emails/modelos/${encodeURIComponent(tipo)}/envios`);

export const cancelarEnvio = (id: string): Promise<void> =>
  requisicaoAutenticada(`/emails/modelos/envios/${id}`, { metodo: 'DELETE' });

/* ─── Variáveis criadas nos e-mails (D-022, parte 2) ─────────────────────── */

export const reconhecerVariaveisDoEmail = (
  tipo: string,
  tokens: readonly string[],
): Promise<{ variaveis: VariavelReconhecida[]; todas: OpcaoDeFonte[] }> =>
  requisicaoAutenticada(`/emails/modelos/${tipo}/variaveis/reconhecimento`, {
    metodo: 'POST',
    corpo: { tokens },
  });

export const criarVariaveisDoEmail = (
  tipo: string,
  variaveis: readonly { readonly token: string; readonly fonte: FonteDaVariavel }[],
): Promise<unknown> =>
  requisicaoAutenticada(`/emails/modelos/${tipo}/variaveis`, {
    metodo: 'POST',
    corpo: { variaveis },
  });
